import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { ServiceQueryResult } from "@/types/serviceQueryTypes";
import { useRegionDetails } from "./services/useRegionDetails";
import { useCacheManager } from "./services/useCacheManager";
import { useNetworkAwareQuery } from "./services/useNetworkAwareQuery";
import { useServiceFetcher } from "./services/useServiceFetcher";
import { usePageSizeAdjuster } from "./services/usePageSizeAdjuster";
import { QUERY_VERSION } from "@/constants/serviceQueryConstants";
import { handleServiceError } from "@/utils/simpleErrorHandler";
import { usePrefetchServices } from "./services/usePrefetchServices";
import { useQueryReset } from "./services/useQueryReset";
import { useErrorRecovery } from "./services/useErrorRecovery";
import { addLog } from '@/utils/enhancedLogging';

export const useServicesQuery = (
  searchQuery: string, 
  activeSubcategoryId: number | null, 
  selectedRegion: string,
  page = 1,
  pageSize = 12
) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Get network and device status from our hook
  const {
    isOnline,
    isMobile,
    isLowData,
    networkStatus,
    hasToasted,
    setHasToasted,
    hasRecoveredFromError,
    setHasRecoveredFromError
  } = useNetworkAwareQuery();
  
  // Get error recovery functionality
  const {
    errorRetryCountRef,
    handleRecoverySuccess,
    handleMultipleFailures,
    resetErrorCount
  } = useErrorRecovery();
  
  // Get region data
  const { data: regionData } = useRegionDetails(selectedRegion);
  
  // Get page size adjustment based on network conditions
  const { 
    adjustedPageSize,
    getStaleTime,
    getGcTime
  } = usePageSizeAdjuster(pageSize);
  
  // Get service fetching functionality
  const { fetchServicePage } = useServiceFetcher();
  
  // Get cache management functionality
  const { 
    prefetchNextPage,
    clearCacheAndRetry,
    prefetchedPagesRef
  } = useCacheManager(queryClient, isLowData);
  
  // Clear cache and retry handler
  const handleClearCacheAndRetry = () => {
    // Reset error tracking
    setHasRecoveredFromError(false);
    resetErrorCount();
    
    // Clear ALL service queries from cache completely
    queryClient.removeQueries({ queryKey: ['services'] });
    
    return clearCacheAndRetry(
      searchQuery, 
      activeSubcategoryId, 
      selectedRegion, 
      page, 
      adjustedPageSize, 
      QUERY_VERSION
    );
  };

  // Force a refresh on initial component mount or when QUERY_VERSION changes
  useEffect(() => {
    // Store the currently used version in localStorage for cache invalidation
    try {
      const currentStoredVersion = localStorage.getItem('lamulamu_query_version');
      if (currentStoredVersion !== QUERY_VERSION) {
        console.log(`Query version changed from ${currentStoredVersion} to ${QUERY_VERSION}, clearing cache`);
        localStorage.setItem('lamulamu_query_version', QUERY_VERSION);
        handleClearCacheAndRetry();
      }
    } catch (e) {
      // Ignore storage errors
    }
  }, [QUERY_VERSION]);

  // Main query for services with enhanced error logging
  const result = useQuery<ServiceQueryResult>({
    queryKey: ['services', searchQuery, activeSubcategoryId, selectedRegion, page, adjustedPageSize, QUERY_VERSION],
    queryFn: async () => {
      try {
        console.log('Fetching services with:', { 
          searchQuery, 
          activeSubcategoryId, 
          selectedRegion, 
          page, 
          pageSize: adjustedPageSize,
          isLowData,
          version: QUERY_VERSION,
          networkType: networkStatus.effectiveType
        });
        
        const result = await fetchServicePage(
          page, 
          searchQuery, 
          activeSubcategoryId, 
          selectedRegion, 
          adjustedPageSize, 
          regionData
        );
        
        // Check if results came from fallback query
        if (result.isUsingFallback) {
          console.log('Using fallback service results');
          setHasRecoveredFromError(true);
          handleRecoverySuccess(hasRecoveredFromError);
        }
        
        // Log successful results
        console.log(`Retrieved ${result.services?.length || 0} services out of ${result.totalCount || 0} total`);
        
        return {
          services: result.services || [],
          totalCount: result.totalCount || 0,
          currentPage: result.currentPage || page,
          totalPages: result.totalPages || 0,
          isUsingFallback: result.isUsingFallback
        };
      } catch (error: any) {
        // Code 215 (from useServicesQuery.ts:215)
        addLog('error', 'Error in useServicesQuery', 'useServicesQuery:215', error);
        
        handleMultipleFailures(handleClearCacheAndRetry);
        
        // Using our simplified error handler
        return handleServiceError(
          error,
          isOnline,
          hasToasted,
          setHasToasted,
          handleClearCacheAndRetry
        );
      }
    },
    staleTime: getStaleTime(),
    gcTime: getGcTime(),
    refetchOnWindowFocus: !isMobile && !isLowData,
    refetchOnReconnect: !isLowData,
    retry: isLowData ? 1 : 2, // Reduced retries to avoid excessive network usage
    refetchInterval: false,
    networkMode: networkStatus.effectiveType === 'slow-2g' ? 'always' : 'online'
  });

  // Handle prefetching of next page
  usePrefetchServices(
    result,
    page,
    isLowData,
    searchQuery,
    activeSubcategoryId,
    selectedRegion,
    adjustedPageSize,
    QUERY_VERSION,
    regionData,
    fetchServicePage,
    prefetchNextPage,
    prefetchedPagesRef
  );

  // Reset toast flag and prefetched pages when parameters change
  useQueryReset(
    searchQuery,
    activeSubcategoryId,
    selectedRegion,
    adjustedPageSize,
    page,
    setHasToasted,
    prefetchedPagesRef
  );

  return {
    ...result,
    // Add a clearCache method to the returned object
    clearCache: handleClearCacheAndRetry,
    hasRecoveredFromError,
    networkStatus: {
      effectiveType: networkStatus.effectiveType,
      isOnline
    }
  };
};
